import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { routeBase } from '../api/collections';
import { Button } from './Button';

/** The page these refusals share: what happened, why, and the one way on. */
function RefusalPanel({ title, body, action, to }: { title: string; body: ReactNode; action: string; to: string }) {
  const nav = useNavigate();
  return (
    <div style={{ flex: 1, background: '#fafaf9', display: 'grid', placeItems: 'center', padding: 28 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', maxWidth: 420 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 13.5, color: '#6b6b70', lineHeight: 1.5 }}>{body}</span>
        <Button variant="primary" size={34} style={{ marginTop: 6 }} onClick={() => nav(to)}>
          {action}
        </Button>
      </div>
    </div>
  );
}

/**
 * The upload pages, opened by somebody who may read the collection and not
 * write to it.
 *
 * Its own refusal rather than a hidden button, because these pages are reached
 * by ADDRESS as well as by button — a bookmark, a link from a colleague, the
 * back button after being demoted. Without it a viewer filled in a study,
 * chose files, watched them transfer and met the server's 403 at the end,
 * which is the longest possible way to find out.
 */
export function CannotUploadHere({ collectionId }: { collectionId?: string }) {
  return (
    <RefusalPanel
      title="You can’t upload to this collection"
      body="Only editors and administrators of its workspace can add studies. You can still open everything in it."
      action={collectionId ? 'Back to the collection' : 'Back to collections'}
      to={collectionId ? `${routeBase()}/collections/${collectionId}` : `${routeBase()}/collections`}
    />
  );
}

/**
 * A page whose collection has been deleted under it.
 *
 * react-query keeps the last good answer when a refetch fails, so these pages
 * went on showing a collection that was gone: the upload page let a person pick
 * files, watched them transfer and only then showed a 404 that blamed "this
 * upload". They say it up front now, and the only way on is back.
 */
export function CollectionGone({ body }: { body?: string }) {
  return (
    <RefusalPanel
      title="This collection no longer exists"
      body={body ?? 'It was deleted while this page was open — from another tab, or by someone else in this workspace.'}
      action="Back to collections"
      to={`${routeBase()}/collections`}
    />
  );
}
